'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import { LayoutDashboard, FileText, Download, CreditCard, Plus, LogOut, Bell, ChevronRight, CheckCircle2, Clock, AlertCircle, ArrowDownToLine, Sparkles, Zap, Shield, Globe2, MoreHorizontal, TrendingUp, User, Settings, Menu } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


interface Application {
  id: string;
  country: string;
  flag: string;
  visaType: string;
  status: 'completed' | 'processing' | 'draft';
  completion: number;
  createdAt: string;
  updatedAt: string;
  canDownload: boolean;
}

interface DownloadItem {
  id: string;
  name: string;
  country: string;
  flag: string;
  downloadedAt: string;
  size: string;
  pages: number;
}

const mockApplications: Application[] = [
  {
    id: 'app-001',
    country: 'Germany',
    flag: '🇩🇪',
    visaType: 'Schengen Tourist',
    status: 'completed',
    completion: 100,
    createdAt: '2026-04-10',
    updatedAt: '2026-04-18',
    canDownload: true,
  },
  {
    id: 'app-002',
    country: 'Canada',
    flag: '🇨🇦',
    visaType: 'Student Visa',
    status: 'processing',
    completion: 72,
    createdAt: '2026-04-20',
    updatedAt: '2026-04-25',
    canDownload: false,
  },
  {
    id: 'app-003',
    country: 'United Kingdom',
    flag: '🇬🇧',
    visaType: 'Business Visa',
    status: 'draft',
    completion: 35,
    createdAt: '2026-04-24',
    updatedAt: '2026-04-24',
    canDownload: false,
  },
];

const mockDownloads: DownloadItem[] = [
  {
    id: 'dl-001',
    name: 'Germany Schengen Package',
    country: 'Germany',
    flag: '🇩🇪',
    downloadedAt: '2026-04-18',
    size: '4.2 MB',
    pages: 18,
  },
  {
    id: 'dl-002',
    name: 'UAE Tourist Visa Package',
    country: 'UAE',
    flag: '🇦🇪',
    downloadedAt: '2026-03-30',
    size: '3.8 MB',
    pages: 14,
  },
  {
    id: 'dl-003',
    name: 'USA B1/B2 Visitor Package',
    country: 'USA',
    flag: '🇺🇸',
    downloadedAt: '2026-02-15',
    size: '5.1 MB',
    pages: 22,
  },
];

const statusConfig = {
  completed: {
    label: 'Completed',
    icon: CheckCircle2,
    className: 'badge-green',
    bar: 'bg-green-500',
  },
  processing: {
    label: 'Processing',
    icon: Clock,
    className: 'badge-blue',
    bar: 'bg-blue-500',
  },
  draft: {
    label: 'Draft',
    icon: AlertCircle,
    className: 'badge-amber',
    bar: 'bg-amber-400',
  },
};

const navItems = [
  { id: 'overview', label: 'Overview', icon: LayoutDashboard },
  { id: 'applications', label: 'My Applications', icon: FileText },
  { id: 'downloads', label: 'Downloads', icon: Download },
  { id: 'billing', label: 'Billing & Plan', icon: CreditCard },
];

export default function UserDashboard() {
  const [activeTab, setActiveTab] = useState('overview');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const completedCount = mockApplications.filter((a) => a.status === 'completed').length;
  const processingCount = mockApplications.filter((a) => a.status === 'processing').length;

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 w-64 bg-white border-r border-gray-100 flex flex-col transition-transform duration-300 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0 lg:static lg:flex`}
      >
        {/* Logo */}
        <div className="h-16 flex items-center gap-2.5 px-5 border-b border-gray-100">
          <AppLogo size={32} />
          <span className="font-bold text-gray-900 tracking-tight">VisaPilot</span>
          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold bg-blue-50 text-blue-600 border border-blue-100">AI</span>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 py-4 space-y-0.5">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeTab === item.id;
            return (
              <button
                key={item.id}
                onClick={() => { setActiveTab(item.id); setSidebarOpen(false); }}
                className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 ${
                  isActive
                    ? 'bg-blue-50 text-blue-700' :'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <Icon size={17} className={isActive ? 'text-blue-600' : 'text-gray-400'} />
                {item.label}
              </button>
            );
          })}

          <div className="pt-4">
            <Link
              href="/visa-application-wizard"
              className="w-full flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700 transition-all duration-150"
            >
              <Plus size={17} />
              New Application
            </Link>
          </div>
        </nav>

        {/* User */}
        <div className="px-3 py-4 border-t border-gray-100">
          <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-sky-400 flex items-center justify-center text-white text-xs font-bold">
              LR
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900 truncate">Layla Rashidi</p>
              <p className="text-xs text-gray-400 truncate">Free Plan</p>
            </div>
            <Settings size={15} className="text-gray-400 flex-shrink-0" />
          </div>
          <Link
            href="/sign-up-login-screen"
            className="w-full flex items-center gap-3 px-3.5 py-2 mt-1 rounded-lg text-sm font-medium text-gray-500 hover:bg-gray-50 hover:text-gray-700 transition-all duration-150"
          >
            <LogOut size={15} />
            Sign out
          </Link>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/20 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="h-16 bg-white border-b border-gray-100 flex items-center justify-between px-5 lg:px-8 sticky top-0 z-20">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <Menu size={18} />
            </button>
            <div>
              <h1 className="text-base font-bold text-gray-900">
                {navItems.find((n) => n.id === activeTab)?.label ?? 'Dashboard'}
              </h1>
              <p className="text-xs text-gray-400 hidden sm:block">Welcome back, Layla 👋</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button className="relative p-2 rounded-lg hover:bg-gray-100 transition-colors">
              <Bell size={17} className="text-gray-500" />
              <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 bg-blue-500 rounded-full" />
            </button>
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-sky-400 flex items-center justify-center text-white text-xs font-bold">
              LR
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 px-5 lg:px-8 py-7 overflow-auto">
          {activeTab === 'overview' && (
            <OverviewTab
              applications={mockApplications}
              completedCount={completedCount}
              processingCount={processingCount}
              onTabChange={setActiveTab}
            />
          )}
          {activeTab === 'applications' && (
            <ApplicationsTab applications={mockApplications} />
          )}
          {activeTab === 'downloads' && (
            <DownloadsTab downloads={mockDownloads} />
          )}
          {activeTab === 'billing' && <BillingTab />}
        </main>
      </div>
    </div>
  );
}

/* ─── Overview Tab ─── */
function OverviewTab({
  applications,
  completedCount,
  processingCount,
  onTabChange,
}: {
  applications: Application[];
  completedCount: number;
  processingCount: number;
  onTabChange: (tab: string) => void;
}) {
  return (
    <div className="space-y-7 animate-fade-in">
      {/* Stats row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Total Applications', value: applications.length, icon: FileText, color: 'text-blue-600', bg: 'bg-blue-50' },
          { label: 'Completed', value: completedCount, icon: CheckCircle2, color: 'text-green-600', bg: 'bg-green-50' },
          { label: 'In Progress', value: processingCount, icon: Clock, color: 'text-amber-600', bg: 'bg-amber-50' },
          { label: 'Downloads', value: 3, icon: Download, color: 'text-purple-600', bg: 'bg-purple-50' },
        ].map((stat) => {
          const Icon = stat.icon;
          return (
            <div key={stat.label} className="card p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-medium text-gray-400 mb-1">{stat.label}</p>
                  <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                </div>
                <div className={`w-9 h-9 rounded-lg ${stat.bg} flex items-center justify-center`}>
                  <Icon size={17} className={stat.color} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Recent applications */}
      <div className="card">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-50">
          <h2 className="text-sm font-bold text-gray-900">Recent Applications</h2>
          <button
            onClick={() => onTabChange('applications')}
            className="text-xs font-semibold text-blue-600 hover:text-blue-700 flex items-center gap-1"
          >
            View all <ChevronRight size={13} />
          </button>
        </div>
        <div className="divide-y divide-gray-50">
          {applications.map((app) => (
            <ApplicationRow key={app.id} app={app} />
          ))}
        </div>
      </div>

      {/* Upgrade banner */}
      <UpgradeBanner />
    </div>
  );
}

/* ─── Application Row ─── */
function ApplicationRow({ app }: { app: Application }) {
  const cfg = statusConfig[app.status];
  const StatusIcon = cfg.icon;

  return (
    <div className="flex items-center gap-4 px-6 py-4 hover:bg-gray-50/60 transition-colors">
      <div className="w-10 h-10 rounded-xl bg-gray-50 border border-gray-100 flex items-center justify-center text-xl flex-shrink-0">
        {app.flag}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <p className="text-sm font-semibold text-gray-900 truncate">{app.country}</p>
          <span className={`badge ${cfg.className} text-xs`}>
            <StatusIcon size={10} />
            {cfg.label}
          </span>
        </div>
        <p className="text-xs text-gray-400 mb-2">{app.visaType}</p>
        {/* Progress bar */}
        <div className="flex items-center gap-2">
          <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${cfg.bar}`}
              style={{ width: `${app.completion}%` }}
            />
          </div>
          <span className="text-xs font-semibold text-gray-500 w-8 text-right">{app.completion}%</span>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {app.canDownload ? (
          <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-50 text-green-700 text-xs font-semibold hover:bg-green-100 transition-colors">
            <ArrowDownToLine size={13} />
            Download
          </button>
        ) : (
          <Link
            href="/visa-application-wizard"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-50 text-blue-700 text-xs font-semibold hover:bg-blue-100 transition-colors"
          >
            Continue
            <ChevronRight size={13} />
          </Link>
        )}
      </div>
    </div>
  );
}

/* ─── Applications Tab ─── */
function ApplicationsTab({ applications }: { applications: Application[] }) {
  return (
    <div className="space-y-5 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-gray-900">My Applications</h2>
          <p className="text-sm text-gray-400 mt-0.5">{applications.length} applications total</p>
        </div>
        <Link href="/visa-application-wizard" className="btn-primary text-sm py-2 px-4">
          <Plus size={15} />
          New Application
        </Link>
      </div>

      <div className="card divide-y divide-gray-50">
        {applications.map((app) => {
          const cfg = statusConfig[app.status];
          const StatusIcon = cfg.icon;
          return (
            <div key={app.id} className="p-6 hover:bg-gray-50/50 transition-colors">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-gray-50 border border-gray-100 flex items-center justify-center text-2xl flex-shrink-0">
                  {app.flag}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <h3 className="text-sm font-bold text-gray-900">{app.country} — {app.visaType}</h3>
                    <span className={`badge ${cfg.className}`}>
                      <StatusIcon size={10} />
                      {cfg.label}
                    </span>
                  </div>
                  <p className="text-xs text-gray-400 mb-3">
                    Created {app.createdAt} · Last updated {app.updatedAt}
                  </p>
                  {/* Progress */}
                  <div className="flex items-center gap-3 mb-4">
                    <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full ${cfg.bar}`}
                        style={{ width: `${app.completion}%` }}
                      />
                    </div>
                    <span className="text-xs font-bold text-gray-700">{app.completion}% complete</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {app.canDownload ? (
                      <button className="btn-secondary text-xs py-1.5 px-3">
                        <ArrowDownToLine size={13} />
                        Download Package
                      </button>
                    ) : (
                      <Link href="/visa-application-wizard" className="btn-primary text-xs py-1.5 px-3">
                        Continue Application
                        <ChevronRight size={13} />
                      </Link>
                    )}
                    <button className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors text-gray-400">
                      <MoreHorizontal size={15} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─── Downloads Tab ─── */
function DownloadsTab({ downloads }: { downloads: DownloadItem[] }) {
  return (
    <div className="space-y-5 animate-fade-in">
      <div>
        <h2 className="text-lg font-bold text-gray-900">Download History</h2>
        <p className="text-sm text-gray-400 mt-0.5">{downloads.length} packages downloaded</p>
      </div>

      <div className="card divide-y divide-gray-50">
        {downloads.map((item) => (
          <div key={item.id} className="flex items-center gap-4 px-6 py-4 hover:bg-gray-50/50 transition-colors">
            <div className="w-10 h-10 rounded-xl bg-gray-50 border border-gray-100 flex items-center justify-center text-xl flex-shrink-0">
              {item.flag}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900 truncate">{item.name}</p>
              <p className="text-xs text-gray-400 mt-0.5">
                Downloaded {item.downloadedAt} · {item.size} · {item.pages} pages
              </p>
            </div>
            <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-50 text-blue-700 text-xs font-semibold hover:bg-blue-100 transition-colors flex-shrink-0">
              <ArrowDownToLine size={13} />
              Re-download
            </button>
          </div>
        ))}
      </div>

      {downloads.length === 0 && (
        <div className="card p-12 flex flex-col items-center text-center">
          <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center mb-4">
            <Download size={24} className="text-gray-400" />
          </div>
          <p className="text-sm font-semibold text-gray-700 mb-1">No downloads yet</p>
          <p className="text-xs text-gray-400">Complete a visa application to download your package.</p>
        </div>
      )}
    </div>
  );
}

/* ─── Billing Tab ─── */
function BillingTab() {
  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h2 className="text-lg font-bold text-gray-900">Billing & Plan</h2>
        <p className="text-sm text-gray-400 mt-0.5">Manage your subscription and payments</p>
      </div>

      {/* Current plan */}
      <div className="card p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-1">Current Plan</p>
            <h3 className="text-xl font-bold text-gray-900">Free Plan</h3>
            <p className="text-sm text-gray-500 mt-1">Basic access — 1 application, no AI generation</p>
          </div>
          <span className="badge badge-gray">Free</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-400">
          <Shield size={13} />
          No credit card required
        </div>
      </div>

      {/* Upgrade options */}
      <UpgradeOptions />
    </div>
  );
}

/* ─── Upgrade Banner ─── */
function UpgradeBanner() {
  return (
    <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-blue-600 via-blue-700 to-sky-600 p-6 text-white">
      <div className="absolute top-0 right-0 w-48 h-48 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/4" />
      <div className="absolute bottom-0 left-1/3 w-32 h-32 bg-white/5 rounded-full translate-y-1/2" />
      <div className="relative flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Sparkles size={16} className="text-yellow-300" />
            <span className="text-xs font-bold uppercase tracking-widest text-blue-200">Upgrade to Full Package</span>
          </div>
          <h3 className="text-lg font-bold mb-1">Get your complete visa package</h3>
          <p className="text-sm text-blue-100">AI cover letter, itinerary, sponsor letter + PDF bundle</p>
        </div>
        <div className="flex-shrink-0">
          <Link
            href="/visa-application-wizard"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-white text-blue-700 font-bold text-sm rounded-xl hover:bg-blue-50 transition-colors"
          >
            Upgrade — $39.99
            <ChevronRight size={15} />
          </Link>
        </div>
      </div>
    </div>
  );
}

/* ─── Upgrade Options ─── */
function UpgradeOptions() {
  const plans = [
    {
      name: 'Visa Setup',
      price: '$9.99',
      period: 'one-time',
      description: 'Guidance + document checklist',
      features: ['AI requirement analysis', 'Document checklist', 'Embassy notes', 'Risk warnings'],
      cta: 'Get Setup',
      highlight: false,
      icon: Globe2,
    },
    {
      name: 'Full Visa Package',
      price: '$39.99',
      period: 'one-time',
      description: 'Everything you need to apply',
      features: ['Everything in Setup', 'AI cover letter', 'Itinerary generation', 'Sponsor letter', 'PDF bundle download', 'Multi-language export'],
      cta: 'Get Full Package',
      highlight: true,
      icon: Sparkles,
    },
    {
      name: 'Agency Basic',
      price: '$299',
      period: '/month',
      description: '200 tokens · For travel agencies',
      features: ['200 tokens/month', 'Client management', 'Bulk applications', 'Priority support'],
      cta: 'Start Agency Plan',
      highlight: false,
      icon: Zap,
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {plans.map((plan) => {
        const Icon = plan.icon;
        return (
          <div
            key={plan.name}
            className={`card p-6 flex flex-col ${
              plan.highlight ? 'ring-2 ring-blue-500 shadow-md shadow-blue-100' : ''
            }`}
          >
            {plan.highlight && (
              <div className="flex items-center gap-1.5 mb-3">
                <span className="badge badge-blue text-xs">
                  <TrendingUp size={10} />
                  Most Popular
                </span>
              </div>
            )}
            <div className={`w-9 h-9 rounded-lg mb-3 flex items-center justify-center ${plan.highlight ? 'bg-blue-600' : 'bg-gray-100'}`}>
              <Icon size={17} className={plan.highlight ? 'text-white' : 'text-gray-500'} />
            </div>
            <h3 className="text-base font-bold text-gray-900 mb-0.5">{plan.name}</h3>
            <p className="text-xs text-gray-400 mb-3">{plan.description}</p>
            <div className="flex items-baseline gap-1 mb-4">
              <span className="text-2xl font-bold text-gray-900">{plan.price}</span>
              <span className="text-xs text-gray-400">{plan.period}</span>
            </div>
            <ul className="space-y-2 mb-5 flex-1">
              {plan.features.map((f) => (
                <li key={f} className="flex items-center gap-2 text-xs text-gray-600">
                  <CheckCircle2 size={13} className="text-green-500 flex-shrink-0" />
                  {f}
                </li>
              ))}
            </ul>
            <Link
              href="/visa-application-wizard"
              className={`w-full text-center text-sm font-semibold py-2.5 rounded-lg transition-all duration-150 ${
                plan.highlight
                  ? 'bg-blue-600 text-white hover:bg-blue-700' :'bg-gray-50 text-gray-700 border border-gray-200 hover:bg-gray-100'
              }`}
            >
              {plan.cta}
            </Link>
          </div>
        );
      })}
    </div>
  );
}
